import React, { useEffect } from 'react';
import * as Yup from "yup";
import { Col, Row, Input, Label, Button, Form } from "reactstrap";
import { useQuery } from 'react-query';
import { queryKeyes } from 'shared/queryKeys';
import { ErrorMessage, Field, FieldProps, Formik } from 'formik';
import ReportDetailsHeader from 'Components/ReportDetailsHeader';
import { errorToast, successToast } from 'Components/Toasts';
import ToolTip from 'Components/ToolTip';
import { TooltipMsg } from 'Components/ToolTipMessage';
import FormValuesDebug from 'utils/debugTools/FormValuesDebug';
import apiGlobal from 'global/api.global';
import { LoadConditionConstant, ReportingTypes, VesselTypeConstant } from 'shared/constants';
import { loadBallastDetailsObject, loadBillOfLadingObject, loadCargoDetails, loadDraftsDisplacement } from 'VesselMaster/vesselMaster.hooks';
import { DraftsDisplacementValidationMessages, commonValidationMessages } from 'Components/ValidationErrorMessages';
import Loading from 'Components/Loading';
import { queryClient } from 'react-query/queryClient';
import { errResponse } from 'GenericForms/Helper';
import env from 'environment/env';

interface DraftsCargoLadingType {
    record: any,
    vessel: any,
    VesselID: number,
    VoyageID: number,
    ReportID: number,
    activeTab: number,
    toggleTab: any
}

const DraftsCargoLadingComponent = ({
    record,
    vessel,
    VesselID,
    VoyageID,
    ReportID,
    activeTab,
    toggleTab
}: DraftsCargoLadingType) => {
    /** State variables */
    // const [blFigureAttachment, setBlFigureAttachment] = useState<Array<any>>([]);
    // const [surveyAttachment, setSurveyAttachment] = useState<Array<any>>([]);
    // const [letterofProtestAttachment, setLetterofProtestAttachment] = useState<Array<any>>([]);
    // const [blFigureAttachmentBool, setBlFigureAttachmentBool] = useState(false);
    // const [surveyAttachmentBool, setSurveyAttachmentBool] = useState(false);
    // const [letterofProtestAttachmentBool, setLetterofProtestAttachmentBool] = useState(false);
    /** State variables end */

    /** Queries */
    /** Drafts & Displacement object used for edit */
    const { data: DraftsDisplacementObject, isLoading: DraftsDisplacementObjectLoading, isError: DraftsDisplacementObjectError }:
        { data: any, isLoading: any, isError: any } = useQuery(
            [queryKeyes.vessel.DraftsDisplacement.key, VesselID, ReportID],
            async () => {
                return await loadDraftsDisplacement(VesselID, ReportID);
            },
            { staleTime: Infinity }
        )
    /** Cargo Details object used for edit */
    const { data: CargoDetailsObject, isLoading: CargoDetailsObjectLoading, isError: CargoDetailsObjectError }:
        { data: any, isLoading: any, isError: any } = useQuery(
            [queryKeyes.vessel.CargoDetails.key, VesselID, ReportID],
            async () => {
                return await loadCargoDetails(VesselID, ReportID);
            },
            { staleTime: Infinity }
        )
    /** Ballast Details object used for edit */
    const { data: BallastDetailsObject, isLoading: BallastDetailsObjectLoading, isError: BallastDetailsObjectError }:
        { data: any, isLoading: any, isError: any } = useQuery(
            [queryKeyes.vessel.BallastDetailsObject.key, VesselID, ReportID],
            async () => {
                return await loadBallastDetailsObject(VesselID, ReportID);
            },
            { staleTime: Infinity }
        )
    /** Bill of Lading object used for edit */
    const { data: BillOfLadingObject, isLoading: BillOfLadingObjectLoading, isError: BillOfLadingObjectError }:
        { data: any, isLoading: any, isError: any } = useQuery(
            [queryKeyes.vessel.BillOfLadingObject.key, VesselID, ReportID],
            async () => {
                return await loadBillOfLadingObject(VesselID, ReportID);
            },
            { staleTime: Infinity }
        )
    /** Queries end */

    /** Assign values to initial object of Drafts & Displacement */
    const getInitialDraftsDisplacement = () => {
        if (DraftsDisplacementObject?.length > 0 && DraftsDisplacementObject[0].id > 0) {
            return DraftsDisplacementObject[0];
        } else {
            return {
                fwd_draft: null,
                draft_mid: null,
                draft_aft: null,
                displacement: null,
                trim: 0,
                vessel_name: VesselID,
                voyage_information: VoyageID,
                vessel_reporting_information: ReportID
            }
        }
    }
    /** Assign values to initial object of Cargo Details */
    const getInitialCargoDetails = () => {
        if (CargoDetailsObject?.length > 0 && CargoDetailsObject[0]?.id > 0) {
            return CargoDetailsObject[0];
        } else {
            return {
                cargo_detail: "",
                amount_of_cargo_loaded: 0,
                amount_of_cargo_discharged: 0,
                total_amount_of_cargo_onboard: 0,
                no_of_reefer_container: 0,
                vessel_name: VesselID,
                voyage_information: VoyageID,
                vessel_reporting_information: ReportID
            }
        }
    }
    /** Assign values to initial object of Ballast Details */
    const getInitialBallastDetails = () => {
        if (BallastDetailsObject?.length > 0 && BallastDetailsObject[0]?.id > 0) {
            return BallastDetailsObject[0];
        } else {
            return {
                permanent: null,
                addition: null,
                total: null,
                loaded: null,
                discharged: null,
                vessel_name: VesselID,
                voyage_information: VoyageID,
                vessel_reporting_information: ReportID
            }
        }
    }
    /** Assign values to initial object of Bill of Lading */
    const getInitialBillOfLading = () => {
        if (BillOfLadingObject?.length > 0 && BillOfLadingObject[0]?.id > 0) {
            return BillOfLadingObject[0];
        } else {
            return {
                is_bill_of_lading_issued_since_last_report: false,
                cargo_survey: false,
                letter_of_protest: false,
                bl_figure: 0,
                //bl_figure_attachment: null as any,
                ship_figure_of_bl_figure: 0,
                //survey_attachment: null as any,
                surveyor_figure_of_bl_figure: 0,
                //letter_of_protest_attachment: null as any,
                vessel_name: VesselID,
                voyage_information: VoyageID,
                vessel_reporting_information: ReportID
            }
        }
    }
    /** Assign values to initial object of Reefer Conatiner Details */
    const getInitialReeferContainerDetails = () => {
        if (vessel?.vessel_type === VesselTypeConstant.CONTAINER) {
            // if (BallastDetailsObject?.length > 0 && BallastDetailsObject[0]?.id > 0) {
            //     return BallastDetailsObject[0];
            // } else {
            return {
                loaded_container: 0,
                discharged_container: 0,
                total_onboard_container: 0,
                vessel_name: VesselID,
                voyage_information: VoyageID,
                vessel_reporting_information: ReportID
            }
        } else {
            return null;
        }
        // }
    }

    /** UseEffect */
    useEffect(() => {
        DraftCargoLadingFormik.initialValues = {
            draftsDisplacement: getInitialDraftsDisplacement(),
            cargoDetails: getInitialCargoDetails(),
            ballastDetails: getInitialBallastDetails(),
            billofLading: getInitialBillOfLading(),
            reeferContainer: getInitialReeferContainerDetails(),
        }
    }, [DraftsDisplacementObject, CargoDetailsObject, BillOfLadingObject])
    /** UseEffect end */

    const DraftCargoLadingFormik = {
        initialValues: {
            draftsDisplacement: getInitialDraftsDisplacement(),
            cargoDetails: getInitialCargoDetails(),
            ballastDetails: getInitialBallastDetails(),
            billofLading: getInitialBillOfLading(),
            reeferContainer: getInitialReeferContainerDetails(),
        },
        validationSchema: Yup.object().shape({
            draftsDisplacement:
                Yup.object({
                    fwd_draft: Yup.string().matches(/^\d{0,2}(?:\.\d{1,2})?$/, commonValidationMessages.before2after2)
                        .required(commonValidationMessages.required),
                    draft_mid: Yup.string().matches(/^\d{0,2}(?:\.\d{1,2})?$/, commonValidationMessages.before2after2)
                        .required(commonValidationMessages.required),
                    draft_aft: Yup.string().matches(/^\d{0,2}(?:\.\d{1,2})?$/, commonValidationMessages.before2after2)
                        .required(commonValidationMessages.required),
                    displacement: Yup.string().matches(/^\d{0,6}(?:\.\d{1,2})?$/, DraftsDisplacementValidationMessages.displacement)
                        .required(commonValidationMessages.required),
                }),
            cargoDetails:
                Yup.object({
                    cargo_detail: Yup.string().min(2, `${commonValidationMessages.min2}`).max(50, `${commonValidationMessages.max50}`),
                    amount_of_cargo_loaded: Yup.string().matches(/^\d{0,7}(?:\.\d{1,2})?$/, commonValidationMessages.before7after2),
                    amount_of_cargo_discharged: Yup.string().matches(/^\d{0,7}(?:\.\d{1,2})?$/, commonValidationMessages.before7after2),
                    total_amount_of_cargo_onboard: Yup.string().matches(/^\d{0,7}(?:\.\d{1,2})?$/, commonValidationMessages.before7after2),
                    no_of_reefer_container: Yup.string().min(1, `${commonValidationMessages.min1}`).max(5, `${commonValidationMessages.max50}`),
                }),
            ballastDetails:
                Yup.object({
                    permanent: Yup.string().matches(/^\d{0,7}(?:\.\d{1,2})?$/, commonValidationMessages.before7after2)
                        .required(commonValidationMessages.required),
                    addition: Yup.string().matches(/^\d{0,7}(?:\.\d{1,2})?$/, commonValidationMessages.before7after2)
                        .required(commonValidationMessages.required),
                    total: Yup.string().matches(/^\d{0,7}(?:\.\d{1,2})?$/, commonValidationMessages.before7after2)
                        .required(commonValidationMessages.required),
                    loaded: Yup.string().matches(/^\d{0,7}(?:\.\d{1,2})?$/, commonValidationMessages.before7after2)
                        .required(commonValidationMessages.required),
                    discharged: Yup.string().matches(/^\d{0,7}(?:\.\d{1,2})?$/, commonValidationMessages.before7after2)
                        .required(commonValidationMessages.required),
                }),
            billofLading:
                Yup.object({
                    is_bill_of_lading_issued_since_last_report: Yup.boolean(),
                    cargo_survey: Yup.boolean(),
                    letter_of_protest: Yup.boolean(),
                    bl_figure: Yup.string().matches(/^\d{0,7}(?:\.\d{1,2})?$/, commonValidationMessages.before7after2),
                    //bl_figure_attachment: Yup.string().nullable(),
                    ship_figure_of_bl_figure: Yup.string().matches(/^\d{0,7}(?:\.\d{1,2})?$/, commonValidationMessages.before7after2),
                    //survey_attachment: Yup.string().nullable(),
                    surveyor_figure_of_bl_figure: Yup.string().matches(/^\d{0,7}(?:\.\d{1,2})?$/, commonValidationMessages.before7after2),
                    //letter_of_protest_attachment: Yup.string().nullable(),
                })
        })
    }

    return (
        <>
            <div className="text-center mb-4">
                <Row>
                    <Col>
                        <h4 className="page_title pos-start mb-0">Drafts & Displacement</h4>
                        <p className="card-title-desc pos-start">All readings since last report</p>
                    </Col>
                    <Col>
                        <ReportDetailsHeader />
                    </Col>
                </Row>
            </div>
            {(DraftsDisplacementObjectLoading ||
                CargoDetailsObjectLoading ||
                BillOfLadingObjectLoading ||
                BallastDetailsObjectLoading) && <Loading message='Loading required data!' />}
            {DraftsDisplacementObjectError && getInitialDraftsDisplacement()}
            {CargoDetailsObjectError && getInitialCargoDetails()}
            {BallastDetailsObjectError && getInitialBallastDetails()}
            {BillOfLadingObjectError && getInitialBillOfLading()}
            {!(DraftsDisplacementObjectLoading ||
                CargoDetailsObjectLoading ||
                BillOfLadingObjectLoading) &&
                <Formik
                    onSubmit={(values: any, actions: any) => {
                        actions.setSubmitting(false);
                        const handleResponse = (response: any) => {
                            if (response.status === 201 || response.status === 200) {
                                successToast("Data saved successfully!");
                            }
                        }
                        /** Drafts & Displacement submit */
                        if (DraftsDisplacementObject && DraftsDisplacementObject[0]?.id > 0) {
                            apiGlobal.put(`/draft_and_displacement/${values.draftsDisplacement.id}/`, values.draftsDisplacement
                            ).then(res => {
                                handleResponse(res);
                                queryClient.invalidateQueries(queryKeyes.vessel.DraftsDisplacement.key);
                                if (env?.form_validation === true) {
                                    toggleTab(activeTab + 1);
                                }
                            })
                                .catch(err => {
                                    if (errResponse.includes(err.response.status)) {
                                        errorToast("Internal error occured, please contact the admin");
                                    }
                                });
                        } else {
                            apiGlobal.post(`/draft_and_displacement/`, values.draftsDisplacement
                            ).then(res => {
                                handleResponse(res);
                                queryClient.invalidateQueries(queryKeyes.vessel.DraftsDisplacement.key);
                                if (env?.form_validation === true) {
                                    toggleTab(activeTab + 1);
                                }
                            })
                                .catch(err => {
                                    if (errResponse.includes(err.response.status)) {
                                        errorToast("Internal error occured, please contact the admin");
                                    }
                                });
                        }
                        /**Cargo Details/ Ballast Details submit */
                        if (BallastDetailsObject && BallastDetailsObject[0]?.id) {
                            apiGlobal.put(`/ballast_details/${values?.ballastDetails.id}/`, values?.ballastDetails
                            ).then(res => {
                                handleResponse(res);
                                queryClient.invalidateQueries(queryKeyes.vessel.BallastDetailsObject.key);
                            })
                                .catch(err => {
                                    if (errResponse.includes(err.response.status)) {
                                        errorToast("Internal error occured, please contact the admin");
                                    }
                                });
                        } else {
                            apiGlobal.post(`/ballast_details/`, values.ballastDetails
                            ).then(res => {
                                handleResponse(res);
                                queryClient.invalidateQueries(queryKeyes.vessel.BallastDetailsObject.key);
                            })
                                .catch(err => {
                                    if (errResponse.includes(err.response.status)) {
                                        errorToast("Internal error occured, please contact the admin");
                                    }
                                });
                        }
                        if (record && record?.load_condition === LoadConditionConstant.LADEN) {
                            if (CargoDetailsObject && CargoDetailsObject[0]?.id) {
                                apiGlobal.put(`/cargo_detail/${values.cargoDetails.id}/`, values.cargoDetails
                                ).then(res => {
                                    handleResponse(res);
                                    queryClient.invalidateQueries(queryKeyes.vessel.CargoDetails.key);
                                })
                                    .catch(err => {
                                        if (errResponse.includes(err.response.status)) {
                                            errorToast("Internal error occured, please contact the admin");
                                        }
                                    });
                            } else {
                                apiGlobal.post(`/cargo_detail/`, values.cargoDetails
                                ).then(res => {
                                    handleResponse(res);
                                    queryClient.invalidateQueries(queryKeyes.vessel.CargoDetails.key);
                                })
                                    .catch(err => {
                                        if (errResponse.includes(err.response.status)) {
                                            errorToast("Internal error occured, please contact the admin");
                                        }
                                    });
                            }
                        }
                        /** Bill of Lading submit */
                        if (record && record.load_condition === LoadConditionConstant.LADEN) {
                            if (BillOfLadingObject?.length > 0 && BillOfLadingObject[0]?.id > 0) {
                                //     if (blFigureAttachment) {
                                //         values.billofLading.bl_figure_attachment = blFigureAttachment[blFigureAttachment.length - 1];
                                //     }
                                //     if (surveyAttachment) {
                                //         values.billofLading.survey_attachment = surveyAttachment[surveyAttachment.length - 1];
                                //     }
                                //     if (letterofProtestAttachment) {
                                //         values.billofLading.letter_of_protest_attachment = letterofProtestAttachment[letterofProtestAttachment.length - 1];
                                //    }
                                apiGlobal.put(`/bill_of_lading/${values.billofLading.id}/`, values.billofLading).then(res => {
                                    handleResponse(res);
                                    queryClient.invalidateQueries(queryKeyes.vessel.BillOfLadingObject.key);
                                }).catch(err => {
                                    if (errResponse.includes(err.response.status)) {
                                        errorToast("Internal error occured, please contact the admin");
                                    }
                                });
                            } else {
                                // if (blFigureAttachment.length <= 0) {
                                //     values.billofLading.bl_figure_attachment = null;
                                // } else {
                                //     values.billofLading.bl_figure_attachment = blFigureAttachment[0];
                                // }
                                // if (surveyAttachment.length <= 0) {
                                //     values.billofLading.survey_attachment = null;
                                // } else {
                                //     values.billofLading.survey_attachment = surveyAttachment[0];
                                // }
                                // if (letterofProtestAttachment.length <= 0) {
                                //     values.billofLading.letter_of_protest_attachment = null;
                                // } else {
                                //     values.billofLading.letter_of_protest_attachment = letterofProtestAttachment[0];
                                // }
                                apiGlobal.post(`/bill_of_lading/`, values.billofLading).then(res => {
                                    handleResponse(res);
                                    queryClient.invalidateQueries(queryKeyes.vessel.BillOfLadingObject.key);
                                }).catch(err => {
                                    if (errResponse.includes(err.response.status)) {
                                        errorToast("Internal error occured, please contact the admin");
                                    }
                                });
                            }
                        }
                        /** Reefer Containers submit */
                        if (vessel?.vessel_type === VesselTypeConstant.CONTAINER &&
                            record?.reporting_type === ReportingTypes.IN_PORT) {
                            apiGlobal.post(`/reefer_container_details/`, values.reeferContainer
                            ).then(res => {
                                handleResponse(res);
                                queryClient.invalidateQueries(queryKeyes.vessel.CargoDetails.key);
                            })
                                .catch(err => {
                                    if (errResponse.includes(err.response.status)) {
                                        errorToast("Internal error occured, please contact the admin");
                                    }
                                });
                        }
                    }}
                    initialValues={DraftCargoLadingFormik.initialValues}
                    validationSchema={DraftCargoLadingFormik.validationSchema}
                >
                    {({ values, errors, handleSubmit, handleChange }: { values: any, errors: any, handleSubmit: any, handleChange: any, touched: any }) => (
                        <Form autoComplete="off" onSubmit={handleSubmit} noValidate>
                            <>
                                <Row>
                                    <Col>
                                        <Label className="asteric mb-0" for="fwd_draft">
                                            Fwd draft<i className='bx bx-info-circle ml-2p' id='fwd_draft_msg'></i>
                                        </Label>
                                        <ToolTip target='fwd_draft_msg'
                                            message={`${TooltipMsg.Draft_and_Displacement.filter((item: any) => item.target === 'fwd_draft_msg')
                                                .map((tool: any) => { return tool.message })}`}
                                        />
                                        <div className="mb-3 input-group">
                                            <Field name='draftsDisplacement.fwd_draft'>
                                                {({ field, form }: FieldProps) => (
                                                    <Input
                                                        type="text"
                                                        className="form-control"
                                                        id="fwd_draft"
                                                        name={field.name}
                                                        onChange={(e: any) => {
                                                            handleChange(e);
                                                            form.setFieldValue('draftsDisplacement.trim', (values?.draftsDisplacement.draft_aft - e.target.value).toFixed(2))
                                                        }}
                                                        onBlur={(e: any) => {
                                                            handleChange(e);
                                                            form.setFieldValue('draftsDisplacement.trim', (values?.draftsDisplacement.draft_aft - e.target.value).toFixed(2))
                                                        }}
                                                        defaultValue={values && values?.draftsDisplacement.fwd_draft}
                                                        autoFocus
                                                    />
                                                )}
                                            </Field>
                                            <div className="input-group-text round_border">m</div>
                                            <ErrorMessage name='draftsDisplacement.fwd_draft'>{msg => <Label className='error'>{msg}</Label>}</ErrorMessage>
                                        </div>
                                    </Col>
                                    <Col>
                                        <Label className="asteric mb-0" for="draft_mid">
                                            Mid draft<i className='bx bx-info-circle ml-2p' id='draft_mid_msg'></i>
                                        </Label>
                                        <ToolTip target='draft_mid_msg'
                                            message={`${TooltipMsg.Draft_and_Displacement.filter((item: any) => item.target === 'draft_mid_msg')
                                                .map((tool: any) => { return tool.message })}`}
                                        />
                                        <div className="mb-3 input-group">
                                            <Field
                                                type="text"
                                                className="form-control"
                                                id="draft_mid"
                                                name="draftsDisplacement.draft_mid"
                                            />
                                            <div className="input-group-text round_border">m</div>
                                            <ErrorMessage name='draftsDisplacement.draft_mid'>{msg => <Label className='error'>{msg}</Label>}</ErrorMessage>
                                        </div>
                                    </Col>
                                    <Col>
                                        <Label className="asteric mb-0" for="draft_aft">
                                            Aft draft<i className='bx bx-info-circle ml-2p' id='draft_aft_msg'></i>
                                        </Label>
                                        <ToolTip target='draft_aft_msg'
                                            message={`${TooltipMsg.Draft_and_Displacement.filter((item: any) => item.target === 'draft_aft_msg')
                                                .map((tool: any) => { return tool.message })}`}
                                        />
                                        <div className="mb-3 input-group">
                                            <Field name='draftsDisplacement.draft_aft'>
                                                {({ field, form }: FieldProps) => (
                                                    <Input
                                                        type="text"
                                                        className="form-control"
                                                        id="draft_aft"
                                                        name={field.name}
                                                        onChange={(e: any) => {
                                                            handleChange(e);
                                                            form.setFieldValue('draftsDisplacement.trim', (e.target.value - values?.draftsDisplacement.fwd_draft).toFixed(2))
                                                        }}
                                                        onBlur={(e: any) => {
                                                            handleChange(e);
                                                            form.setFieldValue('draftsDisplacement.trim', (e.target.value - values?.draftsDisplacement.fwd_draft).toFixed(2))
                                                        }}
                                                        defaultValue={values && values?.draftsDisplacement.draft_aft}
                                                    />
                                                )}
                                            </Field>
                                            <div className="input-group-text round_border">m</div>
                                            <ErrorMessage name='draftsDisplacement.draft_aft'>{msg => <Label className='error'>{msg}</Label>}</ErrorMessage>
                                        </div>
                                    </Col>
                                    <Col lg={3}>
                                        <Label className="asteric mb-0" for="trim">
                                            Trim<i className='bx bx-info-circle ml-2p' id='trim_msg'></i>
                                        </Label>
                                        <ToolTip target='trim_msg'
                                            message={`${TooltipMsg.Weather_Data.filter((item: any) => item.target === 'trim_msg')
                                                .map((tool: any) => { return tool.message })}`}
                                        />
                                        <div className="mb-3 input-group">
                                            <Field
                                                type="text"
                                                className="form-control"
                                                id="trim"
                                                name="draftsDisplacement.trim"
                                                disabled
                                            />
                                            <div className="input-group-text round_border">m</div>
                                            <ErrorMessage name='draftsDisplacement.trim'>{msg => <Label className='error'>{msg}</Label>}</ErrorMessage>
                                        </div>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col lg={3}>
                                        <Label className="asteric mb-0" for="displacement">
                                            Displacement<i className='bx bx-info-circle ml-2p' id='displacement_msg'></i>
                                        </Label>
                                        <ToolTip target='displacement_msg'
                                            message={`${TooltipMsg.Draft_and_Displacement.filter((item: any) => item.target === 'displacement_msg')
                                                .map((tool: any) => { return tool.message })}`}
                                        />
                                        <div className="mb-3 input-group">
                                            <Field
                                                type="text"
                                                className="form-control"
                                                id="displacement"
                                                name="draftsDisplacement.displacement"
                                            />
                                            <div className="input-group-text round_border">mt</div>
                                            <ErrorMessage name='draftsDisplacement.displacement'>{msg => <Label className='error'>{msg}</Label>}</ErrorMessage>
                                        </div>
                                    </Col>
                                </Row>
                                {(record && record?.is_this_cargo_loading_discharging === true) &&
                                    <>
                                        <h4 className="mb-0 mt-5">Cargo Details</h4>
                                        <p className="card-title-desc pos-start mb-3">All readings since last report</p>
                                        <Row>
                                            <Col>
                                                <>
                                                    <Label className='asteric mb-0' for="cargo_detail">
                                                        Cargo details<i className='bx bx-info-circle ml-2p' id='cargo_detail_msg'></i>
                                                    </Label>
                                                    <ToolTip target='cargo_detail_msg'
                                                        message={`${TooltipMsg.Draft_and_Displacement.filter((item: any) => item.target === 'cargo_detail_msg')
                                                            .map((tool: any) => { return tool.message })}`}
                                                    />
                                                    <div className="mb-3">
                                                        <Field
                                                            as="textarea"
                                                            className="form-control"
                                                            name="cargoDetails.cargo_detail"
                                                            rows="4"
                                                        />
                                                        <ErrorMessage name='cargoDetails.cargo_detail'>{msg => <Label className='error'>{msg}</Label>}</ErrorMessage>
                                                    </div>
                                                </>
                                            </Col>
                                            <Col>
                                                <Label className='asteric mb-0' for="amount_of_cargo_loaded">
                                                    Amount of cargo loaded<i className='bx bx-info-circle ml-2p' id='amount_of_cargo_loaded_msg'></i>
                                                </Label>
                                                <ToolTip target='amount_of_cargo_loaded_msg'
                                                    message={`${TooltipMsg.Draft_and_Displacement.filter((item: any) => item.target === 'amount_of_cargo_loaded_msg')
                                                        .map((tool: any) => { return tool.message })}`}
                                                />
                                                <div className="mb-3 input-group">
                                                    <Field
                                                        type="text"
                                                        className="form-control"
                                                        id="amount_of_cargo_loaded"
                                                        name="cargoDetails.amount_of_cargo_loaded"
                                                    />
                                                    {vessel?.vessel_type === 'Container' ?
                                                        <div className="input-group-text round_border">TEU</div> :
                                                        <div className="input-group-text round_border">mt</div>
                                                    }
                                                    <ErrorMessage name='cargoDetails.amount_of_cargo_loaded'>{msg => <Label className='error'>{msg}</Label>}</ErrorMessage>
                                                </div>
                                            </Col>
                                            <Col>
                                                <Label className='asteric mb-0' for="amount_of_cargo_discharged">
                                                    Amount of cargo discharged<i className='bx bx-info-circle ml-2p' id='amount_of_cargo_discharged_msg'></i>
                                                </Label>
                                                <ToolTip target='amount_of_cargo_discharged_msg'
                                                    message={`${TooltipMsg.Draft_and_Displacement.filter((item: any) => item.target === 'amount_of_cargo_discharged_msg')
                                                        .map((tool: any) => { return tool.message })}`}
                                                />
                                                <div className="mb-3 input-group">
                                                    <Field
                                                        type="text"
                                                        className="form-control"
                                                        id="amount_of_cargo_discharged"
                                                        name="cargoDetails.amount_of_cargo_discharged"
                                                    />
                                                    {vessel?.vessel_type === 'Container' ?
                                                        <div className="input-group-text">TEU</div> :
                                                        <div className="input-group-text round_border">mt</div>
                                                    }
                                                    <ErrorMessage name='cargoDetails.amount_of_cargo_discharged'>
                                                        {msg => <Label className='error'>{msg}</Label>}
                                                    </ErrorMessage>
                                                </div>
                                            </Col>
                                            <Col>
                                                <Label className='asteric mb-0' for="total_amount_of_cargo_onboard">
                                                    Total cargo onboard<i className='bx bx-info-circle ml-2p' id='total_amount_of_cargo_onboard_msg'></i>
                                                </Label>
                                                <ToolTip target='total_amount_of_cargo_onboard_msg'
                                                    message={`${TooltipMsg.Draft_and_Displacement
                                                        .filter((item: any) => item.target === 'total_amount_of_cargo_onboard_msg')
                                                        .map((tool: any) => { return tool.message })}`}
                                                />
                                                <div className="mb-3 input-group">
                                                    <Field
                                                        type="text"
                                                        className="form-control"
                                                        id="total_amount_of_cargo_onboard"
                                                        name="cargoDetails.total_amount_of_cargo_onboard"
                                                    />
                                                    {vessel?.vessel_type === 'Container' ?
                                                        <div className="input-group-text">TEU</div> :
                                                        <div className="input-group-text round_border">mt</div>
                                                    }
                                                    <ErrorMessage name='cargoDetails.total_amount_of_cargo_onboard'>
                                                        {msg => <Label className='error'>{msg}</Label>}
                                                    </ErrorMessage>
                                                </div>
                                            </Col>
                                        </Row>
                                    </>
                                }
                                {vessel?.vessel_type === VesselTypeConstant.CONTAINER &&
                                    <>
                                        <h4 className="mb-0 mt-5">Reefer Containers</h4>
                                        <p className="card-title-desc pos-start mb-3">All readings since last report</p>
                                        <Row className='mb-3'>
                                            <Col lg={4}>
                                                <Label for="no_of_reefer_containers_loaded" className='mb-0'>No. of reefer containers loaded
                                                    <i className='bx bx-info-circle ml-2p' id='no_of_reefer_containers_loaded_msg' />
                                                </Label>
                                                <ToolTip target='no_of_reefer_containers_loaded_msg'
                                                    message={`${TooltipMsg.Reefer_Container
                                                        .filter((item: any) => item.target === 'no_of_reefer_containers_loaded_msg')
                                                        .map((tool: any) => { return tool.message })}`}
                                                />
                                                <Field
                                                    type="text"
                                                    className="form-control"
                                                    id="no_of_reefer_containers_loaded"
                                                    name='reeferContainer.loaded_container'
                                                />
                                            </Col>
                                            <Col lg={4}>
                                                <Label for="no_of_reefer_containers_discharged" className='mb-0'>No. of reefer containers discharged
                                                    <i className='bx bx-info-circle ml-2p' id='no_of_reefer_containers_discharged_msg' />
                                                </Label>
                                                <ToolTip target='no_of_reefer_containers_discharged_msg'
                                                    message={`${TooltipMsg.Reefer_Container
                                                        .filter((item: any) => item.target === 'no_of_reefer_containers_discharged_msg')
                                                        .map((tool: any) => { return tool.message })}`}
                                                />
                                                <Field
                                                    type="text"
                                                    className="form-control"
                                                    id="no_of_reefer_containers_discharged"
                                                    name='reeferContainer.discharged_container'
                                                />
                                            </Col>
                                            <Col lg={4}>
                                                <Label for="total_reefer_containers_onboard" className='mb-0'>Total reefer containers onboard
                                                    <i className='bx bx-info-circle ml-2p' id='total_reefer_containers_onboard_msg' />
                                                </Label>
                                                <ToolTip target='total_reefer_containers_onboard_msg'
                                                    message={`${TooltipMsg.Reefer_Container
                                                        .filter((item: any) => item.target === 'total_reefer_containers_onboard_msg')
                                                        .map((tool: any) => { return tool.message })}`}
                                                />
                                                <Field
                                                    type="text"
                                                    className="form-control"
                                                    name='reeferContainer.total_onboard_container'
                                                />
                                            </Col>
                                        </Row>
                                    </>
                                }
                                <div className="mt-5 mb-1">
                                    <Row>
                                        <Col lg={3}>
                                            <Label className="mb-0 w-5 mt-3">
                                                Is Bill of Lading issued since last report?
                                                <i className='bx bx-info-circle ml-2p' id='is_bill_of_lading_issued_since_last_report_msg'></i>
                                            </Label>
                                            <ToolTip target='is_bill_of_lading_issued_since_last_report_msg'
                                                message={`${TooltipMsg.Draft_and_Displacement
                                                    .filter((item: any) => item.target === 'is_bill_of_lading_issued_since_last_report_msg')
                                                    .map((tool: any) => { return tool.message })}`}
                                            />
                                        </Col>
                                        <Col lg={1}>
                                            <Field name={'billofLading.is_bill_of_lading_issued_since_last_report'}>
                                                {({ field, form }: FieldProps) => (
                                                    <div className="square-switch sqswitch mt-3 ml-1">
                                                        <Input
                                                            type="checkbox"
                                                            switch="none"
                                                            name={field.name}
                                                            id="is_bill_of_lading_issued_since_last_report"
                                                            onChange={handleChange}
                                                            defaultChecked={values?.billofLading.is_bill_of_lading_issued_since_last_report}
                                                        />
                                                        <Label
                                                            htmlFor="is_bill_of_lading_issued_since_last_report"
                                                            data-on-label='Yes'
                                                            data-off-label='No'>
                                                        </Label>
                                                    </div>
                                                )}
                                            </Field>
                                        </Col>
                                    </Row>
                                </div>
                                {values?.billofLading.is_bill_of_lading_issued_since_last_report === true &&
                                    <>
                                        <h4 className="mb-0">Bill of Lading</h4>
                                        <p className="card-title-desc pos-start mb-3">All readings since last report</p>
                                        <Row>
                                            <Col lg={3}>
                                                <Label className='asteric mb-0' for="bl_figure">
                                                    BL figures (Cargo)<i className='bx bx-info-circle ml-2p' id='bl_figure_msg'></i>
                                                </Label>
                                                <ToolTip target='bl_figure_msg'
                                                    message={`${TooltipMsg.Draft_and_Displacement.filter((item: any) => item.target === 'bl_figure_msg')
                                                        .map((tool: any) => { return tool.message })}`}
                                                />
                                                <div className="mb-3 input-group">
                                                    <Field
                                                        type="text"
                                                        className="form-control"
                                                        id="bl_figure"
                                                        name="billofLading.bl_figure"
                                                    />
                                                    <div className="input-group-text round_border">mt</div>
                                                </div>
                                                <ErrorMessage name='billofLading.bl_figure'>{msg => <Label className='error'>{msg}</Label>}</ErrorMessage>
                                            </Col>
                                            <Col lg={3}>
                                                <Label className='asteric mb-0' for="ship_figure_of_bl_figure">
                                                    Ship figures (Cargo)<i className='bx bx-info-circle ml-2p' id='ship_figure_of_bl_figure_msg'></i>
                                                </Label>
                                                <ToolTip target='ship_figure_of_bl_figure_msg'
                                                    message={`${TooltipMsg.Draft_and_Displacement
                                                        .filter((item: any) => item.target === 'ship_figure_of_bl_figure_msg')
                                                        .map((tool: any) => { return tool.message })}`}
                                                />
                                                <div className="mb-3 input-group">
                                                    <Field
                                                        type="text"
                                                        className="form-control"
                                                        id="ship_figure_of_bl_figure"
                                                        name="billofLading.ship_figure_of_bl_figure"
                                                    />
                                                    <div className="input-group-text round_border">mt</div>
                                                    <ErrorMessage name='billofLading.ship_figure_of_bl_figure'>{msg => <Label className='error'>{msg}</Label>}</ErrorMessage>
                                                </div>
                                            </Col>
                                            {/* <Col lg={3}>
                                                <Label for="bl_figure_attachment" className="mt-3 mb-0">BL figure attachment
                                                    <i className='bx bx-info-circle ml-2p' id='bl_figure_attachment_msg' />
                                                    <i className="ml-1 dripicons-upload icon_s23 mt-3"
                                                        onClick={() => {
                                                            setBlFigureAttachmentBool(true);
                                                        }}
                                                        data-toggle="modal"
                                                        id='bl_figure_attachment'></i>
                                                </Label>
                                                {(BillOfLadingObject?.length > 0 && BillOfLadingObject[0]?.id > 0) ?
                                                    <>
                                                        <br /><Label className='mt-0 primary-label'>{values?.billofLading.bl_figure_attachment.toString().substring(10, 34)}</Label>
                                                    </> : null
                                                }
                                                <ToolTip target='bl_figure_attachment_msg'
                                                    message={`${TooltipMsg.Draft_and_Displacement.filter((item: any) => item.target === 'bl_figure_attachment_msg')
                                                        .map((tool: any) => { return tool.message })}`}
                                                />
                                            </Col>
                                            <ReportingFileUploadModal
                                                state={blFigureAttachmentBool}
                                                stateName={'BLFigureAttachment'}
                                                array={blFigureAttachment}
                                                setArray={setBlFigureAttachment}
                                                modalId={'bl_figure_attachment'}
                                            /> */}
                                        </Row>
                                        <Row>
                                            <Col lg={3}>
                                                <div className="d-flex flex-wrap gap-2 mb-3 mt-2">
                                                    <Label className="mb-0 mt-3 w-10" for='cargo_survey'>
                                                        Cargo survey?<i className='bx bx-info-circle ml-2p' id='cargo_survey_msg'></i>
                                                    </Label>
                                                    <ToolTip target='cargo_survey_msg'
                                                        message={`${TooltipMsg.Draft_and_Displacement.filter((item: any) => item.target === 'cargo_survey_msg')
                                                            .map((tool: any) => { return tool.message })}`}
                                                    />
                                                    <Field name={'billofLading.cargo_survey'}>
                                                        {({ field, form }: FieldProps) => (
                                                            <div className="square-switch sqswitch mt-3">
                                                                <Input
                                                                    type="checkbox"
                                                                    switch="none"
                                                                    name={field.name}
                                                                    id="cargo_survey"
                                                                    onChange={handleChange}
                                                                    defaultChecked={values?.billofLading.cargo_survey}
                                                                />
                                                                <Label
                                                                    htmlFor="cargo_survey"
                                                                    data-on-label='Yes'
                                                                    data-off-label='No'>
                                                                </Label>
                                                            </div>
                                                        )}
                                                    </Field>
                                                </div>
                                            </Col>
                                            {values?.billofLading.cargo_survey === true &&
                                                <>
                                                    <Col lg={3}>
                                                        <Label className="asteric mb-0" for="surveyor_figure_of_bl_figure">
                                                            Surveyor figures (Cargo)<i className='bx bx-info-circle ml-2p' id="surveyor_figure_of_bl_figure_msg"></i>
                                                        </Label>
                                                        <ToolTip target='surveyor_figure_of_bl_figure_msg'
                                                            message={`${TooltipMsg.Draft_and_Displacement
                                                                .filter((item: any) => item.target === 'surveyor_figure_of_bl_figure_msg')
                                                                .map((tool: any) => { return tool.message })}`}
                                                        />
                                                        <div className="mb-3 input-group">
                                                            <Field
                                                                type="text"
                                                                className="form-control"
                                                                id="surveyor_figure_of_bl_figure"
                                                                name="billofLading.surveyor_figure_of_bl_figure"
                                                            />
                                                            <div className="input-group-text round_border">mt</div>
                                                            <ErrorMessage name='billofLading.surveyor_figure_of_bl_figure'>{msg => <Label className='error'>{msg}</Label>}</ErrorMessage>
                                                        </div>
                                                    </Col>
                                                    {/* <Col lg={3}>
                                                        <Label for="survey_attachment" className="mt-3 mb-0">Surveyor figure attachment
                                                            <i className='bx bx-info-circle ml-2p' id='survey_attachment_msg' />
                                                            <i className="ml-1 dripicons-upload icon_s23 mt-3"
                                                                onClick={() => {
                                                                    setSurveyAttachmentBool(true);
                                                                }}
                                                                data-toggle="modal"
                                                                id='survey_attachment'></i>
                                                        </Label>
                                                        {(BillOfLadingObject?.length > 0 && BillOfLadingObject[0]?.id > 0) ?
                                                            <Label className='mt-0 primary-label'>{values?.billofLading.survey_attachment}</Label> : null
                                                        }
                                                        <ToolTip target='survey_attachment_msg'
                                                            message={`${TooltipMsg.Draft_and_Displacement
                                                                .filter((item: any) => item.target === 'survey_attachment_msg')
                                                                .map((tool: any) => { return tool.message })}`}
                                                        />
                                                    </Col>
                                                    <ReportingFileUploadModal
                                                        state={surveyAttachmentBool}
                                                        array={surveyAttachment}
                                                        setArray={setSurveyAttachment}
                                                        modalId={'survey_attachment'}
                                                    /> */}
                                                </>
                                            }
                                        </Row>
                                        {/* <Row>
                                            <Col lg={3}>
                                                <div className="d-flex flex-wrap gap-2 mb-3">
                                                    <Label className="mt-3 w-10">
                                                        Letter of protest?<i className='bx bx-info-circle ml-2p' id='letter_of_protest_msg'></i>
                                                    </Label>
                                                    <ToolTip target='letter_of_protest_msg'
                                                        message={`${TooltipMsg.Draft_and_Displacement.filter((item: any) => item.target === 'letter_of_protest_msg')
                                                            .map((tool: any) => { return tool.message })}`}
                                                    />
                                                    <Field name={'billofLading.letter_of_protest'}>
                                                        {({ field, form }: FieldProps) => (
                                                            <div className="square-switch sqswitch mt-3">
                                                                <Input
                                                                    type="checkbox"
                                                                    switch="none"
                                                                    name={field.name}
                                                                    id="letter_of_protest"
                                                                    onChange={handleChange}
                                                                    defaultChecked={values?.billofLading.letter_of_protest}
                                                                />
                                                                <Label
                                                                    htmlFor="letter_of_protest"
                                                                    data-on-label='Yes'
                                                                    data-off-label='No'>
                                                                </Label>
                                                            </div>
                                                        )}
                                                    </Field>
                                                </div>
                                            </Col>
                                            {values?.billofLading.letter_of_protest &&
                                                <>
                                                    <Col lg={3}>
                                                        <Label for="letter_of_protest_attachment" className="mt-2">Letter of protest attachment
                                                            <i className='bx bx-info-circle ml-2p' id='letter_of_protest_attachment_msg' />
                                                            <i className="ml-1 dripicons-upload icon_s23 mt-2"
                                                                onClick={() => {
                                                                    setLetterofProtestAttachmentBool(true);
                                                                }}
                                                                data-toggle="modal"
                                                                id='letter_of_protest_attachment' />
                                                        </Label>
                                                        {(BillOfLadingObject?.length > 0 && BillOfLadingObject[0]?.id > 0) ?
                                                            <Label className='mt-0 primary-label'>{values?.billofLading.letter_of_protest_attachment}</Label> : null
                                                        }
                                                        <ToolTip target='letter_of_protest_attachment_msg'
                                                            message={`${TooltipMsg.Draft_and_Displacement
                                                                .filter((item: any) => item.target === 'letter_of_protest_attachment_msg')
                                                                .map((tool: any) => { return tool.message })}`}
                                                        />
                                                    </Col>
                                                    <ReportingFileUploadModal
                                                        state={letterofProtestAttachmentBool}
                                                        array={letterofProtestAttachment}
                                                        setArray={setLetterofProtestAttachment}
                                                        modalId={'letter_of_protest_attachment'}
                                                    />
                                                </>
                                            }
                                        </Row> */}
                                    </>
                                }
                                <h4 className="mb-0 mt-5">Ballast Details</h4>
                                <p className="card-title-desc pos-start mb-3">All readings since last report</p>
                                <Row>
                                    <Col>
                                        <Label className='asteric mb-0' for="permanent">
                                            Permanent ballast<i className='bx bx-info-circle ml-2p' id='permanent_msg'></i>
                                        </Label>
                                        <ToolTip target='permanent_msg'
                                            message={`${TooltipMsg.Draft_and_Displacement.filter((item: any) => item.target === 'permanent_msg')
                                                .map((tool: any) => { return tool.message })}`}
                                        />
                                        <div className="input-group mb-3">
                                            <Field
                                                type="text"
                                                className="form-control"
                                                id="permanent"
                                                name="ballastDetails.permanent"
                                            />
                                            <div className="input-group-text round_border">mt</div>
                                            <ErrorMessage name='ballastDetails.permanent'>{msg => <Label className='error'>{msg}</Label>}</ErrorMessage>
                                        </div>
                                    </Col>
                                    <Col>
                                        <Label className='asteric mb-0' for="addition">
                                            Additional ballast<i className='bx bx-info-circle ml-2p' id='addition_msg'></i>
                                        </Label>
                                        <ToolTip target='addition_msg'
                                            message={`${TooltipMsg.Draft_and_Displacement.filter((item: any) => item.target === 'addition_msg')
                                                .map((tool: any) => { return tool.message })}`}
                                        />
                                        <div className="input-group mb-3">
                                            <Field
                                                type="text"
                                                className="form-control"
                                                id="addition"
                                                name="ballastDetails.addition"
                                            />
                                            <div className="input-group-text round_border">mt</div>
                                            <ErrorMessage name='ballastDetails.addition'>{msg => <Label className='error'>{msg}</Label>}</ErrorMessage>
                                        </div>
                                    </Col>
                                    <Col>
                                        <Label className='asteric mb-0' for="total">
                                            Total ballast<i className='bx bx-info-circle ml-2p' id='total_msg'></i>
                                        </Label>
                                        <ToolTip target='total_msg'
                                            message={`${TooltipMsg.Draft_and_Displacement.filter((item: any) => item.target === 'total_msg')
                                                .map((tool: any) => { return tool.message })}`}
                                        />
                                        <div className="input-group mb-3">
                                            <Field
                                                type="text"
                                                className="form-control"
                                                id="total"
                                                name="ballastDetails.total"
                                            />
                                            <div className="input-group-text round_border">mt</div>
                                            <ErrorMessage name='ballastDetails.total'>{msg => <Label className='error'>{msg}</Label>}</ErrorMessage>
                                        </div>
                                    </Col>
                                </Row>
                                <h4 className="mb-0 mt-5">Ballast Operations</h4>
                                <p className="card-title-desc pos-start mb-3">All readings since last report</p>
                                <Row>
                                    <Col lg={4}>
                                        <Label className='asteric mb-0' for="loaded">
                                            Loaded<i className='bx bx-info-circle ml-2p' id='loaded_msg'></i>
                                        </Label>
                                        <ToolTip target='loaded_msg'
                                            message={`${TooltipMsg.Draft_and_Displacement.filter((item: any) => item.target === 'loaded_msg')
                                                .map((tool: any) => { return tool.message })}`}
                                        />
                                        <div className="input-group mb-3">
                                            <Field
                                                type="text"
                                                className="form-control"
                                                id="loaded"
                                                name="ballastDetails.loaded"
                                            />
                                            <div className="input-group-text round_border">mt</div>
                                            <ErrorMessage name='ballastDetails.loaded'>{msg => <Label className='error'>{msg}</Label>}</ErrorMessage>
                                        </div>
                                    </Col>
                                    <Col lg={4}>
                                        <Label className='asteric mb-0' for="discharged">
                                            Discharged<i className='bx bx-info-circle ml-2p' id='discharged_msg'></i>
                                        </Label>
                                        <ToolTip target='discharged_msg'
                                            message={`${TooltipMsg.Draft_and_Displacement.filter((item: any) => item.target === 'discharged_msg')
                                                .map((tool: any) => { return tool.message })}`}
                                        />
                                        <div className="input-group mb-3">
                                            <Field
                                                type="text"
                                                className="form-control"
                                                id="discharged"
                                                name="ballastDetails.discharged"
                                            />
                                            <div className="input-group-text round_border">mt</div>
                                            <ErrorMessage name='ballastDetails.discharged'>{msg => <Label className='error'>{msg}</Label>}</ErrorMessage>
                                        </div>
                                    </Col>
                                </Row>
                            </>
                            <Row className="ele_row1">
                                <div className="d-flex flex-wrap gap-5 mt-3 btn_Margin">
                                    <Button type="submit" color="primary" className="btn_size_cstm pos-end" onClick={(e) => {
                                        if (env?.form_validation === false) {
                                            toggleTab(activeTab + 1);
                                        }
                                    }}>Next <i className="bx bx-chevron-right ms-1" /></Button>
                                    <Button type="button" color="primary" className="btn_size_cstm pos-start-0_75" onClick={(e) => {
                                        toggleTab(activeTab - 1);
                                    }}><i className="bx bx-chevron-left me-1" /> Previous</Button>
                                </div>
                            </Row>
                            <FormValuesDebug values={[values, errors, DraftCargoLadingFormik.initialValues]} />
                        </Form>
                    )}
                </Formik >
            }
        </>
    )
}

export default DraftsCargoLadingComponent